import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Observable, of, ReplaySubject} from 'rxjs';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {FormHelperService} from '../../../shared/services/form-helper.service';
import {Router} from '@angular/router';
import {delay, first, takeUntil, tap} from 'rxjs/operators';
import {Uuid} from '../../../shared/interface/response/uuid';
import {HttpErrorResponse} from '@angular/common/http';
import {Department} from '../../../shared/interface/department';
import {DepartmentService} from '../../../shared/services/department.service';

@Component({
  selector: 'app-department-add',
  templateUrl: './department-add.component.html',
  styleUrls: ['./department-add.component.scss']
})
export class DepartmentAddComponent implements OnInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() department?: Observable<Department>;
  @Input() departmentObject?: Department;
  @Output() closeModification = new EventEmitter<any>();

  departmentForm: FormGroup;
  parentDepartmentControl: FormControl;
  supervisorControl: FormControl;
  processingRequest = false;
  formErrors = {};

  constructor(
    private fb: FormBuilder,
    private snack: MatSnackBar,
    private formHelper: FormHelperService,
    private router: Router,
    private departmentService: DepartmentService,
  ) {
    this.departmentForm = fb.group({
      name: fb.control('', [Validators.required]),
      parentDepartment: this.parentDepartmentControl = fb.control(''),
      supervisor: this.supervisorControl = fb.control(''),
    });
  }

  ngOnInit(): void {
    if (this.department) {
      this.department
        .pipe(takeUntil(this.destroyed$))
        .subscribe((department: Department) => {
          this.initialize(department);
          this.departmentObject = department;
        });
    }
  }

  initialize(department: Department): void {
    of(department)
      .pipe(
        delay(0),
        tap(() => {
          this.departmentForm.reset({
            name: department.name,
            parentDepartment: department.parentDepartment?.uuid,
            supervisor: department.supervisor?.uuid,
          });
        })
      ).subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  save(): void {
    if (!this.departmentForm.valid) {
      this.departmentForm.markAllAsTouched();
      this.snack.open('Wypełnij lub popraw wszystkie wymagane pola', '', {
        duration: 2000,
      });
      return;
    }

    this.processingRequest = true;
    this.formHelper.removeBackendErrorState(this.departmentForm, this.formErrors);
    this.formErrors = {};

    this.departmentService.sendForm(this.departmentForm.getRawValue(), this.departmentObject?.uuid)
      .pipe(
        first()
      )
      .subscribe((response: Uuid) => {
        this.snack.open(this.departmentObject ? 'Pomyślnie zaktualizowano dział'
          : 'Pomyślnie utworzono dział', '', {
          duration: 2000,
        });
        this.processingRequest = false;

        if (this.department) {
          this.closeModification.emit();
        } else {
          this.router.navigate(['/uzytkownicy/dzialy/szczegoly', response.uuid]);
        }
      }, (error: HttpErrorResponse) => {
        this.processingRequest = false;
        this.snack.open(this.departmentObject ? 'Podczas aktualizacji działu wystąpił błąd'
          : 'Podczas tworzenia działu wystąpił błąd', error.status.toString(), {
          duration: 2000,
        });

        if (error.status === 400) {
          this.formErrors = error.error.error.list;
          this.formHelper.setBackendErrorState(this.departmentForm, this.formErrors);
        }
      })
    ;
  }

  cancel(): void {
    if (this.department) {
      this.closeModification.emit();
    } else {
      this.router.navigate(['/uzytkownicy/dzialy']);
    }
  }

}
